import {useEffect, useState, useCallback, useRef} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

export const useURLSync = (filters, setFilters) => {
	const history = useHistory();
	const location = useLocation();
	const [isInitialSyncComplete, setIsInitialSyncComplete] = useState(false);
	const isUpdatingFromRedux = useRef(false);
	const lastURLUpdateRef = useRef(null);

	const parseFilterValue = useCallback((value) => {
		if (!value) return [];
		if (Array.isArray(value)) return value;

		try {
			const parsed = JSON.parse(value);
			if (Array.isArray(parsed)) return parsed.map(String);
		} catch (e) {
			// Ignore
		}

		return value.split(',').filter(v => v.trim()).map(String);
	}, []);

	const serializeFilterValue = useCallback((value) => {
		if (!value) return '';
		if (Array.isArray(value)) {
			return value.filter(v => v).join(',');
		}
		return value.toString();
	}, []);

	// Sync URL to Redux ONCE on mount
	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);
		const updates = {};

		// Simple filters
		const minPrice = urlParams.get('min');
		const maxPrice = urlParams.get('max');
		const sortBy = urlParams.get('sort');
		const promotion = urlParams.get('promotion');
		const oos = urlParams.get('oos');
		const searchQuery = urlParams.get('q');

		// Array filters
		const brand_id = urlParams.get('brand_id');
		const cat_id = urlParams.get('cat_id');
		const sub_cat_id = urlParams.get('sub_cat_id');
		const country_id = urlParams.get('country_id');
		const diet_id = urlParams.get('diet_id');

		if (brand_id) updates.brand_id = parseFilterValue(brand_id);
		if (cat_id) updates.cat_id = parseFilterValue(cat_id);
		if (sub_cat_id) updates.sub_cat_id = parseFilterValue(sub_cat_id);
		if (country_id) updates.country_id = parseFilterValue(country_id);
		if (diet_id) updates.diet_id = parseFilterValue(diet_id);

		if (minPrice) updates.min = parseInt(minPrice) || 0;
		if (maxPrice) updates.max = parseInt(maxPrice) || 1000;
		if (promotion) updates.promotion = promotion === '1' || promotion === 'true';
		if (oos) updates.oos = oos === '1' || oos === 'true';
		if (searchQuery) updates.q = searchQuery;
		if (sortBy) updates.sort = sortBy;

		if (Object.keys(updates).length > 0) {
			setFilters({...filters, ...updates});
		}

		setTimeout(() => setIsInitialSyncComplete(true), 100);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Sync Redux to URL (only when filters change and not from URL update)
	useEffect(() => {
		if (!isInitialSyncComplete || isUpdatingFromRedux.current) {
			isUpdatingFromRedux.current = false;
			return;
		}

		const params = new URLSearchParams();

		// Array filters
		if (filters?.cat_id?.length) {
			params.set('cat_id', serializeFilterValue(filters.cat_id));
		}
		if (filters?.sub_cat_id?.length) {
			params.set('sub_cat_id', serializeFilterValue(filters.sub_cat_id));
		}
		if (filters?.brand_id?.length) {
			params.set('brand_id', serializeFilterValue(filters.brand_id));
		}
		if (filters?.country_id?.length) {
			params.set('country_id', serializeFilterValue(filters.country_id));
		}
		if (filters?.diet_id?.length) {
			params.set('diet_id', serializeFilterValue(filters.diet_id));
		}

		// Price range
		if (filters?.min && filters.min > 0) {
			params.set('min', filters.min.toString());
		}
		if (filters?.max && filters.max > 0 && filters.max < 1000) {
			params.set('max', filters.max.toString());
		}

		// Boolean filters
		if (filters?.promotion) params.set('promotion', '1');
		if (filters?.oos) params.set('oos', '1');

		// Search and sort
		if (filters?.q) params.set('q', filters.q);
		if (filters?.sort && filters.sort !== 'a-z') {
			params.set('sort', filters.sort);
		}

		const queryString = params.toString();
		const currentQuery = location.search.slice(1); // Remove '?'

		// Prevent loop: only update URL if it actually changed AND we haven't just updated it
		if (queryString !== currentQuery && lastURLUpdateRef.current !== queryString) {
			lastURLUpdateRef.current = queryString;
			const newUrl = `${location.pathname}${queryString ? `?${queryString}` : ''}`;
			history.replace(newUrl);
		}
	}, [
		// Only specific filter values, not the entire filters object
		// Convert arrays to strings for stable comparison
		filters?.brand_id?.join(','),
		filters?.cat_id?.join(','),
		filters?.sub_cat_id?.join(','),
		filters?.country_id?.join(','),
		filters?.diet_id?.join(','),
		filters?.min,
		filters?.max,
		filters?.promotion,
		filters?.oos,
		filters?.q,
		filters?.sort,
		isInitialSyncComplete,
		location.pathname,
		// Removed location.search - it changes when we update URL, causing infinite loop
		// Removed history and serializeFilterValue - function references don't need to trigger URL sync
	]);

	return {isInitialSyncComplete};
};